import React, { useRef } from 'react';
import DatePickerLib from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { FiChevronDown, FiX } from 'react-icons/fi';
import { LuCalendarDays } from 'react-icons/lu';

interface CustomDatePickerProps {
  label?: string;
  error?: string;
  wrapperClassName?: string;
  className?: string;
  selected?: Date | null;
  onChange?: (date: Date | null) => void;
  placeholderText?: string;
  disabled?: boolean;
  readOnly?: boolean;
  minDate?: Date;
  maxDate?: Date;
  showTimeSelect?: boolean;
  showTimeSelectOnly?: boolean;
  timeFormat?: string;
  dateFormat?: string;
  /** When true, show an explicit clear (X) control once a value is set. */
  isClearable?: boolean;
  [key: string]: any;
}

const DatePicker: React.FC<CustomDatePickerProps> = ({
  wrapperClassName,
  className,
  // Jun-18 (a user V1 test, R2-1 follow-up): intercept isClearable so we render
  // our OWN prominent clear button instead of react-datepicker's tiny default X
  // (the reviewer kept asking "how do I remove a selected date" because that X
  // was too subtle, sitting next to the chevron). Not forwarding it to the lib
  // avoids a double clear control.
  isClearable,
  ...rest
}) => {
  const datePickerRef = useRef<DatePickerLib | null>(null);

  const datePickerClassName =
    'flex items-center gap-2 w-fit border-2 rounded-md px-3 py-1 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500';
  const datePickerInputClassName = 'border-none outline-none grow';

  const r = rest as any;
  const hasValue = Boolean(r.selected || r.startDate || r.endDate);
  const showClear =
    Boolean(isClearable) && hasValue && !r.disabled && !r.readOnly;

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    // selectsRange hands the consumer a [start, end] tuple; single mode a Date.
    if (r.selectsRange) {
      r.onChange?.([null, null], e);
    } else {
      r.onChange?.(null, e);
    }
    datePickerRef.current?.setOpen(false);
  };

  return (
    <div className={`${datePickerClassName} ${wrapperClassName}`}>
      <LuCalendarDays
        size={20}
        className='text-body-500 flex-shrink-0 cursor-pointer'
        onClick={() => datePickerRef.current?.setOpen(true)}
      />
      <DatePickerLib
        ref={datePickerRef}
        wrapperClassName='flex-grow'
        className={`${datePickerInputClassName} ${className ?? ''}`}
        {...rest}
      />
      {showClear ? (
        <button
          type='button'
          aria-label='Clear selected dates'
          title='Clear dates'
          onClick={handleClear}
          className='flex-shrink-0 flex items-center justify-center rounded-full p-0.5 text-body-500 hover:text-body-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer'
        >
          <FiX size={18} />
        </button>
      ) : (
        <FiChevronDown
          size={20}
          className='text-body-500 flex-shrink-0 cursor-pointer'
          onClick={() => datePickerRef.current?.setOpen(true)}
        />
      )}
    </div>
  );
};

export default DatePicker;
